import { useEffect, useState } from 'react';
import { Globe } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../utils/supabase';
import { API_BASE_URL } from '../../utils/apiConfig';
import { useUserContext } from '../../contexts/UserContext';

/* "Sell online" — whether a plan, service or product is offered in the member
   app / web store. Shown only for gyms with the store feature on (Gympe-
   enabled); everyone else sees no change. */

const cache = new Map<string, Promise<{ storeFeature: boolean; storeOpen: boolean }>>();

async function authed(path: string, init: RequestInit = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${session?.access_token}`, 'Content-Type': 'application/json', ...(init.headers || {}) },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new Error(json.error || 'Request failed');
  return json;
}

/** Whether to show the store controls for the current gym (fetched once per gym). */
export function useStoreFeature(): { storeFeature: boolean; storeOpen: boolean } {
  const { userContext } = useUserContext();
  const gymId = userContext?.accessibleGyms?.[0];
  const [state, setState] = useState({ storeFeature: false, storeOpen: false });
  useEffect(() => {
    if (!gymId) return;
    if (!cache.has(gymId)) {
      cache.set(gymId, authed(`/api/commerce/capabilities?gymId=${gymId}`)
        .then(j => ({ storeFeature: !!j.data.storeFeature, storeOpen: !!j.data.storeOpen }))
        .catch(() => ({ storeFeature: false, storeOpen: false })));
    }
    let live = true;
    cache.get(gymId)!.then(s => { if (live) setState(s); });
    return () => { live = false; };
  }, [gymId]);
  return state;
}

export function SellOnlineSwitch({ endpoint, sellOnline, onChanged, compact }: {
  /** e.g. `/api/products/<id>` — must accept PUT { sellOnline } */
  endpoint: string;
  sellOnline: boolean;
  onChanged: (next: boolean) => void;
  compact?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const toggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await authed(endpoint, { method: 'PUT', body: JSON.stringify({ sellOnline: !sellOnline }) });
      onChanged(!sellOnline);
      toast.success(!sellOnline ? 'Now sold online' : 'Removed from the online store');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      title={sellOnline ? 'Sold in the member app — click to stop' : 'Not sold online — click to offer it in the member app'}
      className={`inline-flex items-center gap-1.5 rounded-lg border transition-colors disabled:opacity-50 ${compact ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1.5 text-xs'} font-bold ${
        sellOnline ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-gray-400 border-gray-200 hover:text-gray-600'
      }`}
    >
      <Globe className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      {sellOnline ? 'Online' : 'Offline'}
    </button>
  );
}
